import React from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { useThemeToggle } from '../hooks/useThemeToggle';

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
`;

const BrandPanel = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #0f172a 0%, #1a7a70 50%, #2d9a8f 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: rgba(78, 184, 173, 0.1);
    top: -100px;
    right: -200px;
  }

  &::after {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: rgba(78, 184, 173, 0.08);
    bottom: -100px;
    left: -100px;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const BrandContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 400px;
`;

const BrandLogo = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 2rem;
`;

const BrandTitle = styled.h1`
  color: white;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const BrandSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.0625rem;
  line-height: 1.7;
`;

const BrandFeatures = styled.div`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  text-align: left;
`;

const BrandFeature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9375rem;
`;

const FeatureIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.8125rem;
`;

const FormPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  background: var(--bg-primary);
  max-width: 540px;
  min-width: 380px;
  position: relative;

  @media (max-width: 900px) {
    max-width: 100%;
    min-width: 0;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const ThemeTogglePos = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
`;

const ThemeBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 1.125rem;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const AuthLayout: React.FC = () => {
  const { mode, toggle } = useThemeToggle();

  return (
    <Wrapper>
      <BrandPanel>
        <BrandContent>
          <BrandLogo>
            <svg viewBox="0 0 32 32" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
              <rect width="32" height="32" rx="7" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
              <path d="M16 4Q16 16 4 16Q16 16 16 28Q16 16 28 16Q16 16 16 4Z" fill="white" />
              <circle cx="24" cy="7" r="1.8" fill="white" opacity="0.6" />
            </svg>
          </BrandLogo>
          <BrandTitle>Luminafactory</BrandTitle>
          <BrandSubtitle>
            The intelligent agent harness platform. Build, deploy, and manage AI
            agents for your business.
          </BrandSubtitle>
          <BrandFeatures>
            <BrandFeature>
              <FeatureIcon>&#x2728;</FeatureIcon>
              <span>
                Configure agents with natural language, not complex forms
              </span>
            </BrandFeature>
            <BrandFeature>
              <FeatureIcon>&#x1F50D;</FeatureIcon>
              <span>Test in sandbox, inspect every exchange in real time</span>
            </BrandFeature>
            <BrandFeature>
              <FeatureIcon>&#x1F680;</FeatureIcon>
              <span>Provision partners and manage budgets from one portal</span>
            </BrandFeature>
          </BrandFeatures>
        </BrandContent>
      </BrandPanel>

      <FormPanel>
        <ThemeTogglePos>
          <ThemeBtn onClick={toggle} title="Toggle theme">
            {mode === 'light' ? '\u263E' : '\u2600'}
          </ThemeBtn>
        </ThemeTogglePos>

        <Outlet />
      </FormPanel>
    </Wrapper>
  );
};

export default AuthLayout;
